import { supabase } from "./supabase";

// Cache implementation for performance
const cache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

const getCachedData = (key) => {
  const cached = cache.get(key);
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    console.log(`Cache hit for ${key}`);
    return cached.data;
  }
  return null;
};

const setCachedData = (key, data) => {
  cache.set(key, { data, timestamp: Date.now() });
  console.log(`Cached data for ${key}`);
};

const DEFAULT_TOTALS = {
  available: 0,
  pending: 0,
  paid: 0,
  lifetime: 0,
  unpaidBonuses: 0,
};

const transformPayoutData = (
  affiliateName,
  weeklyRows = [],
  totals = DEFAULT_TOTALS,
  options = {}
) => {
  const safeTotals = {
    available: totals.available ?? 0,
    pending: totals.pending ?? 0,
    paid: totals.paid ?? 0,
    lifetime: totals.lifetime ?? 0,
    unpaidBonuses: totals.unpaidBonuses ?? 0,
  };

  const weeklyEarnings = weeklyRows.map((row) => ({
    weekStart: row.week_start_date ?? row.weekStart ?? null,
    weekEnd: row.week_end_date ?? row.weekEnd ?? null,
    amount: Number(row.week_total_earnings ?? row.total_earnings ?? row.amount ?? 0),
    commission: Number(row.week_commission ?? row.total_commission ?? 0),
    referralBonus: Number(row.week_referral_bonus ?? row.referral_bonus ?? 0),
    totalSpend: Number(row.week_spend ?? row.total_spend ?? 0),
    totalRevenue: Number(row.week_revenue ?? row.total_revenue ?? 0),
    roas: Number(row.week_roas ?? row.avg_roas ?? 0),
    adCount: Number(row.week_ad_count ?? row.ad_count ?? 0),
    purchases: Number(row.week_purchases ?? 0),
    month: row.month_key ?? row.month ?? "",
    monthName: row.month_name ?? row.monthName ?? "",
    status: "available",
  }));

  const monthlyData = {};
  weeklyEarnings.forEach((week) => {
    if (!week.month) return;
    if (!monthlyData[week.month]) {
      monthlyData[week.month] = {
        month: week.month,
        monthName: week.monthName,
        totalAmount: 0,
        totalSpend: 0,
        totalRevenue: 0,
        weeksCount: 0,
        totalAds: 0,
      };
    }
    monthlyData[week.month].totalAmount += week.amount || 0;
    monthlyData[week.month].totalSpend += week.totalSpend || 0;
    monthlyData[week.month].totalRevenue += week.totalRevenue || 0;
    monthlyData[week.month].weeksCount += 1;
    monthlyData[week.month].totalAds += week.adCount || 0;
  });

  return {
    affiliateName,
    weeklyEarnings,
    totals: safeTotals,
    monthlyBreakdown: Object.values(monthlyData),
    isProvisional: Boolean(options?.isProvisional),
  };
};

const loadTotalsFromBalance = async (affiliateName) => {
  const [{ data: balanceRows, error: balanceError }, { data: paidRows, error: paidError }] =
    await Promise.all([
      supabase.from("affiliate_payout_balances").select("*").eq("affiliate_name", affiliateName),
      supabase
        .from("payout_transaction")
        .select("amount")
        .eq("affiliate_name", affiliateName)
        .eq("status", "paid"),
    ]);

  if (balanceError) {
    console.error("Error loading payout balance row:", balanceError);
  }
  if (paidError) {
    console.error("Error loading paid totals:", paidError);
  }

  const balanceRow = Array.isArray(balanceRows) ? balanceRows[0] : balanceRows;
  const paidTotal = Array.isArray(paidRows)
    ? paidRows.reduce((sum, row) => sum + Number(row.amount || 0), 0)
    : 0;

  if (!balanceRow) {
    return {
      available: 0,
      pending: 0,
      paid: paidTotal,
      lifetime: 0,
      unpaidBonuses: 0,
    };
  }

  return {
    available: Number(balanceRow.total_unpaid || 0),
    pending: Number(balanceRow.pending_total || 0),
    paid: paidTotal,
    lifetime:
      Number(balanceRow.lifetime_commission || 0) + Number(balanceRow.lifetime_referral || 0),
    unpaidBonuses: Number(balanceRow.unpaid_referral || 0),
  };
};

// Helper functions for week calculation
const getWeekStart = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Monday
  return new Date(d.setDate(diff));
};

const getWeekEnd = (weekStart) => {
  const end = new Date(weekStart);
  end.setDate(end.getDate() + 6);
  return end;
};

// Fallback that queries daily table directly (replaces old weekly table fallback)
const fallbackLoadFromDaily = async (affiliateName) => {
  console.warn(`Falling back to direct daily table query for ${affiliateName}`);

  const [{ data: dailyRows, error: dailyError }, totals] = await Promise.all([
    supabase
      .from("finalized_daily_ad_earnings")
      .select("*")
      .eq("affiliate_name", affiliateName)
      .order("ad_date", { ascending: false }),
    loadTotalsFromBalance(affiliateName),
  ]);

  if (dailyError) {
    console.error("Error loading daily data:", dailyError);
  }

  // Aggregate daily to weekly client-side
  const weeklyMap = new Map();
  (dailyRows || []).forEach((row) => {
    const weekStart = getWeekStart(new Date(row.ad_date));
    const key = weekStart.toISOString().split("T")[0];
    if (!weeklyMap.has(key)) {
      weeklyMap.set(key, {
        weekStart: key,
        weekEnd: getWeekEnd(weekStart).toISOString().split("T")[0],
        amount: 0,
        commission: 0,
        totalSpend: 0,
        totalRevenue: 0,
        adCount: 0,
        purchases: 0,
        referralBonus: 0,
      });
    }
    const w = weeklyMap.get(key);
    w.commission += Number(row.commission || 0);
    w.amount += Number(row.commission || 0);
    w.totalSpend += Number(row.spend || 0);
    w.totalRevenue += Number(row.revenue || 0);
    w.adCount += 1;
    w.purchases += Number(row.purchases || 0);
  });

  return {
    affiliateName,
    weeklyEarnings: Array.from(weeklyMap.values()).sort(
      (a, b) => new Date(b.weekStart) - new Date(a.weekStart)
    ),
    monthlyBreakdown: [], // Will be empty in fallback
    totals,
    isProvisional: true,
  };
};

// Legacy fallback for old function (uses new daily fallback)
const fallbackLoad = async (affiliateName) => {
  return fallbackLoadFromDaily(affiliateName);
};

export const getAffiliatePayoutDataOptimized = async (affiliateName) => {
  const cacheKey = `payout_data_${affiliateName}`;
  const cachedResult = getCachedData(cacheKey);
  if (cachedResult) return cachedResult;

  try {
    console.log(`Loading payout data for ${affiliateName}`);

    const { data, error } = await supabase.rpc("get_affiliate_payout_data_optimized", {
      p_affiliate_name: affiliateName,
    });

    if (error) {
      throw error;
    }

    if (!Array.isArray(data) || data.length === 0) {
      const fallback = await fallbackLoad(affiliateName);
      setCachedData(cacheKey, fallback);
      return fallback;
    }

    const weeklyRows = data.filter((row) => row.week_start_date);
    const totalsRow = data[0] || {};

    const totals = {
      available: Number(totalsRow.total_available_amount || 0),
      pending: Number(totalsRow.total_requested_amount || 0),
      paid: Number(totalsRow.total_paid_amount || 0),
      lifetime: Number(totalsRow.total_lifetime_earnings || 0),
      unpaidBonuses: Number(totalsRow.total_referral_bonuses || 0),
    };

    const transformed = transformPayoutData(affiliateName, weeklyRows, totals);
    setCachedData(cacheKey, transformed);
    return transformed;
  } catch (error) {
    console.error("Error loading payout data, using fallback:", error);
    const fallback = await fallbackLoad(affiliateName);
    setCachedData(cacheKey, fallback);
    return fallback;
  }
};

// New v2 function that returns TRUE monthly and weekly data from daily table
export const getAffiliatePayoutDataV2 = async (
  affiliateName,
  { forceRefresh = false } = {}
) => {
  const cacheKey = `payout_data_v2_${affiliateName}`;
  if (!forceRefresh) {
    const cachedResult = getCachedData(cacheKey);
    if (cachedResult) return cachedResult;
  }

  try {
    console.log(`Loading payout data v2 for ${affiliateName}`);

    const { data, error } = await supabase.rpc("get_affiliate_payout_data_v2", {
      p_affiliate_name: affiliateName,
    });

    if (error) {
      throw error;
    }

    if (!Array.isArray(data) || data.length === 0) {
      const fallback = await fallbackLoadFromDaily(affiliateName);
      setCachedData(cacheKey, fallback);
      return fallback;
    }

    // Separate monthly and weekly rows
    const monthlyRows = (data || []).filter((r) => r.row_type === "monthly");
    const weeklyRows = (data || []).filter((r) => r.row_type === "weekly");
    const totalsRow = data[0] || {};

    const result = {
      affiliateName,
      // TRUE monthly breakdown (Dec = Dec 1-31)
      monthlyBreakdown: monthlyRows.map((row) => ({
        month: row.period_name,
        monthKey: row.period_key,
        totalAmount: Number(row.period_total_earnings || 0),
        totalSpend: Number(row.period_spend || 0),
        totalRevenue: Number(row.period_revenue || 0),
        totalAds: Number(row.period_ad_count || 0),
        roas: Number(row.period_roas || 0),
      })),
      // Weekly earnings (same structure as existing weeklyEarnings)
      weeklyEarnings: weeklyRows.map((row) => ({
        weekStart: row.period_start_date,
        weekEnd: row.period_end_date,
        amount: Number(row.period_total_earnings || 0),
        commission: Number(row.period_commission || 0),
        referralBonus: Number(row.period_referral_bonus || 0),
        totalSpend: Number(row.period_spend || 0),
        totalRevenue: Number(row.period_revenue || 0),
        roas: Number(row.period_roas || 0),
        adCount: Number(row.period_ad_count || 0),
        purchases: Number(row.period_purchases || 0),
        periodName: row.period_name,
        month: row.period_key, // ISO week key for filtering
        status: "available",
      })),
      totals: {
        available: Number(totalsRow.total_available_amount || 0),
        pending: Number(totalsRow.total_requested_amount || 0),
        paid: Number(totalsRow.total_paid_amount || 0),
        lifetime: Number(totalsRow.total_lifetime_earnings || 0),
        unpaidBonuses: Number(totalsRow.total_referral_bonuses || 0),
      },
    };

    setCachedData(cacheKey, result);
    return result;
  } catch (error) {
    console.error("Error loading payout data v2, using fallback:", error);
    const fallback = await fallbackLoadFromDaily(affiliateName);
    setCachedData(cacheKey, fallback);
    return fallback;
  }
};

export const getAffiliatePayoutSummaryOptimized = async (affiliateName) => {
  try {
    const payoutData = await getAffiliatePayoutDataOptimized(affiliateName);

    return {
      affiliateName: payoutData.affiliateName,
      totalAvailable: payoutData.totals.available,
      totalPending: payoutData.totals.pending,
      totalPaid: payoutData.totals.paid,
      totalLifetime: payoutData.totals.lifetime,
      totalReferralBonuses: payoutData.totals.unpaidBonuses,
      weeklyEarningsCount: payoutData.weeklyEarnings.length,
      firstWeek: payoutData.weeklyEarnings?.[0]?.weekStart || "none",
      lastWeek:
        payoutData.weeklyEarnings?.[payoutData.weeklyEarnings.length - 1]?.weekStart || "none",
    };
  } catch (error) {
    console.error("Error getting payout summary:", error);
    return {
      affiliateName,
      totalAvailable: 0,
      totalPending: 0,
      totalPaid: 0,
      totalLifetime: 0,
      totalReferralBonuses: 0,
      weeklyEarningsCount: 0,
      firstWeek: "none",
      lastWeek: "none",
    };
  }
};

export const getAffiliatePayoutHistoryOptimized = async (affiliateName) => {
  try {
    const { data, error } = await supabase
      .from("payout_requests")
      .select("*")
      .eq("affiliate_name", affiliateName)
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error fetching payout history:", error);
      throw error;
    }

    return (data || []).map((request) => ({
      id: request.id,
      weekStart: request.week_start_date,
      weekEnd: request.week_end_date,
      amount: request.total_amount,
      status: request.status,
      requestDate: request.created_at,
      processedDate: request.paid_date,
      paypalEmail: request.paypal_email,
      notes: request.notes || "",
    }));
  } catch (error) {
    console.error("Error getting payout history:", error);
    return [];
  }
};

export const submitPayoutRequestOptimized = async (affiliateName, payoutData) => {
  try {
    const { data, error } = await supabase
      .from("payout_requests")
      .insert({
        affiliate_name: affiliateName,
        week_start_date: payoutData.weekStart,
        week_end_date: payoutData.weekEnd,
        total_amount: payoutData.amount,
        paypal_email: payoutData.paypalEmail,
        status: "pending",
        notes: payoutData.notes || "",
      })
      .select()
      .single();

    if (error) {
      console.error("Error submitting payout request:", error);
      throw error;
    }

    cache.delete(`payout_data_${affiliateName}`);

    console.log(`Payout request submitted for ${affiliateName}`);

    return data;
  } catch (error) {
    console.error("Error submitting payout request:", error);
    throw error;
  }
};

export const clearPayoutCache = (affiliateName = null) => {
  if (affiliateName) {
    cache.delete(`payout_data_${affiliateName}`);
    cache.delete(`payout_data_v2_${affiliateName}`);
    console.log(`Cleared cache for ${affiliateName}`);
  } else {
    cache.clear();
    console.log("Cleared all payout cache");
  }
};
